/**
 * FeedbackButton — vertical-tab CTA anchored to the right edge of the viewport.
 *
 * Renders a fixed-position pill flush against the right side of the screen,
 * vertically centered, with the word "Feedback" set in vertical-rl writing
 * mode (reads top-to-bottom). Modeled on the ba.com pattern (operator
 * reference 2026-05-02). When clicked:
 *   1. Captures a screenshot of the current page via html-to-image.toBlob()
 *      BEFORE the modal opens so the modal itself isn't in the screenshot.
 *   2. Opens FeedbackModal, passing the captured Blob (or null +
 *      captureError flag if the capture failed).
 *
 * The button carries `data-feedback-capture-exclude="true"` so
 * BreadcrumbCollector ignores its own clicks when recording the trail and
 * so the html-to-image filter excludes it from the snapshot.
 *
 * @module components/FeedbackButton
 */

import React, { useState } from 'react';
import { toBlob } from 'html-to-image';
import FeedbackModal from './FeedbackModal';

/**
 * Maximum screenshot size (bytes) we let the user submit. Set well below
 * the host nginx body cap so the screenshot leaves plenty of headroom for
 * description text + breadcrumbs + 3 optional 5 MB attachments.
 *
 * Ruth UAT 2026-05-26: a viewport-sized PNG can hit 3+ MB on data-dense
 * pages, which used to trip the host nginx default 1 MB `client_max_body_size`
 * and surface as the generic "Something went wrong" toast. We now downscale
 * the screenshot before passing it to the modal so the wire payload stays
 * predictable regardless of the source page.
 */
const SCREENSHOT_MAX_BYTES = 800 * 1024; // 800 KB
const SCREENSHOT_MIN_SCALE = 0.25;
const SCREENSHOT_INITIAL_SCALE = 0.7;
const SCREENSHOT_JPEG_QUALITY = 0.82;

/**
 * Re-encode a PNG blob as a smaller JPEG if it exceeds the size budget.
 * Iteratively halves the scale until either the encoded size fits or the
 * minimum scale floor is reached. Returns the original blob unchanged if
 * downscaling fails (canvas taint, browser without Blob support, etc.).
 *
 * @param {Blob} blob - Source screenshot blob (typically PNG from html-to-image).
 * @returns {Promise<Blob>} The original blob or a smaller JPEG blob.
 */
async function downscaleScreenshot(blob) {
  if (!blob || blob.size <= SCREENSHOT_MAX_BYTES) return blob;
  // Skip in environments without HTMLCanvasElement.toBlob (jsdom default).
  if (typeof document === 'undefined' || !document.createElement) return blob;

  let imageBitmap;
  try {
    if (typeof createImageBitmap === 'function') {
      imageBitmap = await createImageBitmap(blob);
    } else {
      // Fallback: <img> → canvas
      const url = URL.createObjectURL(blob);
      try {
        const img = await new Promise((resolve, reject) => {
          const el = new Image();
          el.onload = () => resolve(el);
          el.onerror = reject;
          el.src = url;
        });
        imageBitmap = { width: img.naturalWidth, height: img.naturalHeight, _img: img };
      } finally {
        URL.revokeObjectURL(url);
      }
    }
  } catch (_err) {
    return blob; // Couldn't decode — let backend reject if too large.
  }

  let scale = SCREENSHOT_INITIAL_SCALE;
  while (scale >= SCREENSHOT_MIN_SCALE) {
    const w = Math.max(1, Math.round(imageBitmap.width * scale));
    const h = Math.max(1, Math.round(imageBitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return blob;
    ctx.drawImage(imageBitmap._img || imageBitmap, 0, 0, w, h);

    const reEncoded = await new Promise((resolve) => {
      if (typeof canvas.toBlob !== 'function') {
        resolve(null);
        return;
      }
      canvas.toBlob((b) => resolve(b), 'image/jpeg', SCREENSHOT_JPEG_QUALITY);
    });
    if (!reEncoded) return blob;
    if (reEncoded.size <= SCREENSHOT_MAX_BYTES) return reEncoded;
    scale = scale / 2;
  }
  return blob;
}

export { downscaleScreenshot, SCREENSHOT_MAX_BYTES };

/**
 * FeedbackButton component.
 *
 * @param {Object} props
 * @param {Object|null} props.user - Currently authenticated user object.
 * @param {Object|null} props.activeOrg - Active organisation.
 * @param {Object|null} props.activeBusiness - Active business.
 * @param {Array}  props.businesses - List of businesses (passed to modal).
 * @returns {JSX.Element} The floating feedback button + modal.
 */
function FeedbackButton({ user, activeOrg, activeBusiness, businesses }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [screenshotBlob, setScreenshotBlob] = useState(null);
  const [captureError, setCaptureError] = useState(false);
  // Mark UAT 2026-05-27 (GH #152a): html-to-image's toBlob on a complex
  // OCR review screen can take several seconds. Without visual feedback,
  // impatient users click the button multiple times, queueing redundant
  // captures and burning CPU. `capturing` disables the button + swaps
  // the label so a click clearly registers.
  const [capturing, setCapturing] = useState(false);

  /**
   * Capture the current page as a PNG blob using html-to-image, then open the modal.
   *
   * Capture happens BEFORE the modal opens so the modal itself is not included in
   * the screenshot (pitfall 4). If capture fails for any reason (canvas taint,
   * cross-origin images, browser restriction), the modal still opens but
   * captureError is set to true so the UI can show a friendly fallback message.
   *
   * @returns {Promise<void>}
   */
  async function handleClick() {
    // Defensive: prevent multiple in-flight captures (re-entry would
    // overwrite screenshotBlob with whichever finishes last).
    if (capturing) return;
    setCapturing(true);
    let blob = null;
    let failed = false;

    try {
      // Exclude the feedback button itself from the capture (data-feedback-capture-exclude).
      blob = await toBlob(document.body, {
        cacheBust: true,
        pixelRatio: 1,
        // Firefox: html-to-image's normalizeFontFamily calls .trim() on
        // rule.style.fontFamily, which Firefox returns as undefined for some
        // @font-face rules (Chrome coerces to ""). skipFonts bypasses that path.
        // Screenshots render with the local font instead of embedded web fonts.
        skipFonts: true,
        filter: (node) => !node.dataset || !node.dataset.feedbackCaptureExclude,
      });
      // Downscale large captures so the upload payload doesn't trip
      // nginx body-size limits or take forever to upload from mobile.
      if (blob) {
        try {
          blob = await downscaleScreenshot(blob);
        } catch (_e) {
          // Downscale is best-effort; ship the original if anything goes wrong.
        }
      }
    } catch (err) {
      // Capture failure is non-fatal — modal opens without screenshot.
      console.warn('[FeedbackButton] html-to-image capture failed:', err);
      failed = true;
    }

    setScreenshotBlob(blob);
    setCaptureError(failed || blob === null);
    setModalOpen(true);
    setCapturing(false);
  }

  /**
   * Reset modal state and close the dialog.
   */
  function handleClose() {
    setModalOpen(false);
    setScreenshotBlob(null);
    setCaptureError(false);
  }

  return (
    <>
      <button
        type="button"
        className="feedback-button"
        aria-label={capturing ? 'Capturing screenshot for feedback' : 'Open feedback form'}
        aria-busy={capturing}
        data-feedback-capture-exclude="true"
        onClick={handleClick}
        disabled={capturing}
        style={capturing ? { opacity: 0.7, cursor: 'wait' } : undefined}
      >
        {capturing ? 'Capturing…' : 'Feedback'}
      </button>

      {modalOpen && (
        <FeedbackModal
          isOpen={modalOpen}
          onClose={handleClose}
          screenshotBlob={screenshotBlob}
          captureError={captureError}
          user={user}
          activeOrg={activeOrg}
          activeBusiness={activeBusiness}
        />
      )}
    </>
  );
}

export default FeedbackButton;
